import React, { useState } from 'react'
import { BASE_BACKEND_URL } from '../auth'

import { EnvelopeSimpleOpen, ShieldCheck } from '@phosphor-icons/react'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import { Link, useNavigate } from 'react-router-dom'
import TimedToastAlert from '../components/alerts/TimedToastAlert'
import LoginPasswordField from './shared/LoginPasswordField'
import PasswordRulesPanel from './shared/PasswordRulesPanel'
import LoginShell from './shared/LoginShell'
import {
  RESET_PASSWORD_CODE_LENGTH,
  RESET_PASSWORD_CODE_STEP_COPY,
  RESET_PASSWORD_DEFAULT_EXPIRES_IN_MINUTES,
  RESET_PASSWORD_ERROR_MESSAGES,
  RESET_PASSWORD_RESEND_SUCCESS_MESSAGE,
  RESET_PASSWORD_STEP_COPY,
  RESET_PASSWORD_VALIDATION_ERROR_MESSAGE
} from './constants/resetPasswordPageConstants'
import { isValidLoginEmail, LOGIN_FIELD_ERRORS, LOGIN_FIELD_SX } from './shared/loginConstants'
import { isPasswordValid } from './shared/passwordValidation'

function resetErrorMessage(error) {
  return RESET_PASSWORD_ERROR_MESSAGES[error] || RESET_PASSWORD_ERROR_MESSAGES.default
}

function stepCopy(step, expiresInMinutes) {
  if (step === 'email') {
    return RESET_PASSWORD_STEP_COPY.email
  }

  if (step === 'code') {
    return RESET_PASSWORD_CODE_STEP_COPY(expiresInMinutes)
  }

  return RESET_PASSWORD_STEP_COPY.password
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)
  const [message, setMessage] = useState('')
  const [messageVariant, setMessageVariant] = useState('error')
  const [alertId, setAlertId] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [expiresInMinutes, setExpiresInMinutes] = useState(RESET_PASSWORD_DEFAULT_EXPIRES_IN_MINUTES)
  const [emailError, setEmailError] = useState('')
  const canSubmitCode = new RegExp(`^\\d{${RESET_PASSWORD_CODE_LENGTH}}$`).test(code)

  const showError = (text) => {
    setMessageVariant('error')
    setMessage(text)
    setAlertId(Date.now())
  }

  const showSuccess = (text) => {
    setMessageVariant('success')
    setMessage(text)
    setAlertId(Date.now())
  }

  const clearMessage = () => setMessage('')

  const handleCodeChange = (event) => {
    setCode(event.target.value.replace(/\D/g, '').slice(0, RESET_PASSWORD_CODE_LENGTH))
  }

  const validateEmail = () => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setEmailError(LOGIN_FIELD_ERRORS.emailRequired)
      return false
    }
    if (!isValidLoginEmail(trimmedEmail)) {
      setEmailError(LOGIN_FIELD_ERRORS.emailInvalid)
      return false
    }
    setEmailError('')
    return true
  }

  const requestCode = (event) => {
    event.preventDefault()
    if (step === 'email' && !validateEmail()) return

    setMessage('')
    setIsSubmitting(true)

    fetch(`${BASE_BACKEND_URL}/users/password`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: email.trim() })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showError(resetErrorMessage(data.error))
          return
        }

        const nextExpiresInMinutes =
          data.expires_in_minutes || RESET_PASSWORD_DEFAULT_EXPIRES_IN_MINUTES

        setExpiresInMinutes(nextExpiresInMinutes)
        setCode('')
        setStep('code')
        showSuccess(RESET_PASSWORD_RESEND_SUCCESS_MESSAGE(nextExpiresInMinutes))
      })
      .catch(() => showError(resetErrorMessage()))
      .finally(() => setIsSubmitting(false))
  }

  const verifyCode = (event) => {
    event.preventDefault()
    setMessage('')
    setIsSubmitting(true)

    fetch(`${BASE_BACKEND_URL}/users/password/verify`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email.trim(),
        code: code.trim()
      })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showError(resetErrorMessage(data.error))
          return
        }

        if (
          typeof data.reset_password_token !== 'string' ||
          !data.reset_password_token
        ) {
          showError(RESET_PASSWORD_ERROR_MESSAGES.default)
          return
        }

        setResetToken(data.reset_password_token)
        setStep('password')
        setMessage('')
      })
      .catch(() => showError(resetErrorMessage()))
      .finally(() => setIsSubmitting(false))
  }

  const submitNewPassword = (event) => {
    event.preventDefault()
    if (!isPasswordValid(password, passwordConfirmation)) {
      showError(RESET_PASSWORD_VALIDATION_ERROR_MESSAGE)
      return
    }

    setMessage('')
    setIsSubmitting(true)

    fetch(`${BASE_BACKEND_URL}/users/password`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        reset_password_token: resetToken,
        password,
        password_confirmation: passwordConfirmation
      })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showError(resetErrorMessage(data.error) || data.error)
          return
        }

        navigate('/?reset_done=true', { replace: true })
      })
      .catch(() => showError(resetErrorMessage()))
      .finally(() => setIsSubmitting(false))
  }

  const copy = stepCopy(step, expiresInMinutes)
  const canSubmitPassword = isPasswordValid(password, passwordConfirmation)
  const formAlert = message ? (
    <div className="login-form-alert" aria-live="polite">
      <TimedToastAlert
        id={alertId}
        variant="inline"
        severity={messageVariant}
        message={message}
        onClose={clearMessage}
      />
    </div>
  ) : null

  return (
    <LoginShell eyebrow="Recuperación de acceso" title={copy.title} subtitle={copy.subtitle}>
      {step === 'email' ? (
        <>
          <form className="login-main-form" onSubmit={requestCode} noValidate>
            <TextField
              id="reset-email"
              label="Correo electrónico"
              variant="outlined"
              fullWidth
              type="email"
              name="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                if (emailError) setEmailError('')
              }}
              autoComplete="username"
              required
              autoFocus
              error={Boolean(emailError)}
              helperText={emailError || undefined}
              sx={LOGIN_FIELD_SX}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EnvelopeSimpleOpen className="login-field-icon" size={18} />
                    </InputAdornment>
                  )
                }
              }}
            />
            {formAlert}
            <button
              className={`login-submit-btn${isSubmitting ? ' is-loading' : ''}`}
              type="submit"
              disabled={isSubmitting}
            >
              <span className="login-submit-label">Enviar código</span>
              <span className="login-submit-spinner" aria-hidden="true" />
            </button>
          </form>
          <p className="login-form-footer">
            <Link className="login-forgot-link" to="/">
              Iniciar sesión
            </Link>
          </p>
        </>
      ) : null}

      {step === 'code' ? (
        <>
          <form className="login-main-form" onSubmit={verifyCode} noValidate>
            <TextField
              id="reset-code"
              label="Código de 6 dígitos"
              variant="outlined"
              fullWidth
              name="code"
              value={code}
              onChange={handleCodeChange}
              autoComplete="one-time-code"
              autoFocus
              required
              sx={{
                ...LOGIN_FIELD_SX,
                '& .MuiOutlinedInput-input': {
                  ...LOGIN_FIELD_SX['& .MuiOutlinedInput-input'],
                  letterSpacing: '0.04em',
                  fontWeight: 600
                }
              }}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  maxLength: RESET_PASSWORD_CODE_LENGTH,
                  pattern: '[0-9]*'
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ShieldCheck className="login-field-icon" size={18} />
                    </InputAdornment>
                  )
                }
              }}
            />
            {formAlert}
            <button
              className={`login-submit-btn${isSubmitting ? ' is-loading' : ''}`}
              type="submit"
              disabled={isSubmitting || !canSubmitCode}
            >
              <span className="login-submit-label">Verificar</span>
              <span className="login-submit-spinner" aria-hidden="true" />
            </button>
          </form>
          <div className="login-form-footer login-mfa-footer">
            <button
              type="button"
              className="login-forgot-link"
              onClick={requestCode}
              disabled={isSubmitting}
            >
              Reenviar código
            </button>
            <button
              type="button"
              className="login-forgot-link"
              disabled={isSubmitting}
              onClick={() => {
                setStep('email')
                setMessage('')
              }}
            >
              Volver
            </button>
          </div>
        </>
      ) : null}

      {step === 'password' ? (
        <>
          <form className="login-main-form" onSubmit={submitNewPassword} noValidate>
            <LoginPasswordField
              id="reset-password"
              label="Nueva contraseña"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value.slice(0, 15))}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword((current) => !current)}
              autoFocus
            />
            <LoginPasswordField
              id="reset-password-confirmation"
              label="Confirmar contraseña"
              name="passwordConfirmation"
              value={passwordConfirmation}
              onChange={(event) => setPasswordConfirmation(event.target.value.slice(0, 15))}
              showPassword={showPasswordConfirmation}
              onTogglePassword={() => setShowPasswordConfirmation((current) => !current)}
            />
            <PasswordRulesPanel
              password={password}
              passwordConfirmation={passwordConfirmation}
            />
            {formAlert}
            <button
              className={`login-submit-btn${isSubmitting ? ' is-loading' : ''}`}
              type="submit"
              disabled={isSubmitting || !canSubmitPassword}
            >
              <span className="login-submit-label">Cambiar contraseña</span>
              <span className="login-submit-spinner" aria-hidden="true" />
            </button>
          </form>
          <p className="login-form-footer">
            <Link className="login-forgot-link" to="/">
              Iniciar sesión
            </Link>
          </p>
        </>
      ) : null}
    </LoginShell>
  )
}
